import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { apiError, fmtD, fmtDT } from "@/lib/platform";

const blank = { name: "", industry: "", employee_count: "", owner_username: "", owner_password: "", owner_full_name: "" };

export const TenantsPanel = () => {
  const auth = useAuth();
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(blank);

  const load = useCallback(() => api.get("/platform/tenants").then(({ data }) => setTenants(data.tenants)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/platform/tenants", {
        ...f, employee_count: f.employee_count ? Number(f.employee_count) : null,
        industry: f.industry || null, owner_username: f.owner_username || null,
        owner_password: f.owner_password || null, timezone: "Asia/Kolkata",
      });
      toast.success("Organization created");
      setOpen(false); setF(blank); load();
    } catch (e) {
      toast.error(apiError(e, "Could not create organization"));
    }
  };

  const setStatus = async (t, active) => {
    try {
      await api.put(`/platform/tenants/${t.id}/status`, { status: active ? "active" : "suspended" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-3" data-testid="tenants-panel">
      <div className="flex justify-end">
        <Button onClick={() => setOpen(true)} className="bg-[#008080] hover:bg-[#008080]/90" data-testid="tenant-create-button">
          <Plus className="w-4 h-4 mr-1" /> New organization
        </Button>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {tenants.map((t) => (
          <div key={t.id} className={`bg-white border rounded-xl p-4 ${t.id === auth.user?.tenant_id ? "border-[#008080]" : "border-slate-200"}`}
               data-testid={`tenant-card-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Building2 className="w-4 h-4 text-slate-500 shrink-0" />
                <div className="font-semibold text-[#003366] truncate">{t.name}</div>
              </div>
              <Switch checked={t.status !== "suspended"} disabled={t.is_default} onCheckedChange={(v) => setStatus(t, v)}
                      data-testid={`tenant-status-${t.id}`} />
            </div>
            <div className="text-[11px] font-mono text-slate-400 mt-1">{t.id}</div>
            <div className="flex gap-4 mt-3 text-xs text-slate-600">
              <span><b className="font-mono text-[#003366]">{t.user_count}</b> users</span>
              <span><b className="font-mono text-[#003366]">{t.assessment_count}</b> assessments</span>
              <span>{t.industry || "—"}</span>
            </div>
            <div className="text-[11px] text-slate-400 mt-2">Created {fmtD(t.created_at)} · updated {fmtDT(t.updated_at)}</div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="tenant-create-dialog">
          <DialogHeader>
            <DialogTitle>New organization</DialogTitle>
            <DialogDescription>Creates an isolated tenant with default teams and an optional Organization Owner.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {[["name", "Organization name"], ["industry", "Industry"], ["employee_count", "Employee count"],
              ["owner_username", "Owner username / email"], ["owner_full_name", "Owner full name"], ["owner_password", "Owner password (min 8)"]].map(([k, l]) => (
              <div key={k} className="space-y-1">
                <Label>{l}</Label>
                <Input type={k === "owner_password" ? "password" : k === "employee_count" ? "number" : "text"} value={f[k]}
                       onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} data-testid={`tenant-create-${k}`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={f.name.trim().length < 2} className="bg-[#003366] hover:bg-[#003366]/90"
                    data-testid="tenant-create-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const OutboxPanel = () => {
  const [data, setData] = useState({ items: [], provider: "" });
  useEffect(() => { api.get("/platform/notifications/outbox").then(({ data: d }) => setData(d)).catch(() => {}); }, []);
  return (
    <div className="space-y-3" data-testid="outbox-panel">
      <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="outbox-provider-note">
        Email provider: <b>{data.provider || "none"}</b>. Messages are queued here and will be sent once Amazon SES is connected.
      </div>
      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
        {data.items.length === 0 && <div className="p-6 text-xs text-slate-500 text-center">Outbox is empty.</div>}
        {data.items.map((m) => (
          <div key={m.id} className="px-4 py-3 text-xs flex gap-4" data-testid={`outbox-row-${m.id}`}>
            <span className="font-mono text-slate-400 w-36 shrink-0">{fmtDT(m.created_at)}</span>
            <span className="w-48 truncate text-slate-700">{m.to}</span>
            <span className="flex-1 truncate text-[#003366]">{m.subject}</span>
            <span className="uppercase font-semibold text-amber-700">{m.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
